import { cloneElement } from "react";

import { getInitials } from "@oneleet/utils";

import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

import { DEFAULT_COLOR, DEFAULT_SIZE, DEFAULT_VARIANT } from "./constants";

const avatar = cva(
  "flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full",
  {
    variants: {
      variant: {
        soft: "",
        outlined: "border bg-white",
      },
      color: {
        gray: "",
        brand: "",
        red: "",
        green: "",
      },
      size: {
        micro: "size-5",
        tiny: "size-6",
        small: "size-8",
        medium: "size-10",
        large: "size-12",
        huge: "size-14",
      },
    },
    compoundVariants: [
      {
        variant: "soft",
        color: "gray",
        className: "bg-gray-100",
      },
      {
        variant: "outlined",
        color: "gray",
        className: "border-slate-200",
      },
      {
        variant: "soft",
        color: "brand",
        className: "bg-brand-100",
      },
      {
        variant: "outlined",
        color: "brand",
        className: "border-brand-200",
      },
      {
        variant: "soft",
        color: "red",
        className: "bg-red-100",
      },
      {
        variant: "outlined",
        color: "red",
        className: "border-red-200",
      },
      {
        variant: "soft",
        color: "green",
        className: "bg-green-100",
      },
      {
        variant: "outlined",
        color: "green",
        className: "border-green-200",
      },
    ],
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      color: DEFAULT_COLOR,
      size: DEFAULT_SIZE,
    },
  },
);

const textVariants = cva("", {
  variants: {
    color: {
      gray: "text-gray-500",
      brand: "text-brand-500",
      red: "text-red-600",
      green: "text-green-600",
    },
    size: {
      micro: "line-height-[.75rem] text-[.625rem]",
      tiny: "text-xs",
      small: "text-sm",
      medium: "text-base",
      large: "text-lg",
      huge: "text-xl",
    },
  },
  defaultVariants: {
    color: DEFAULT_COLOR,
    size: DEFAULT_SIZE,
  },
});

const iconVariants = cva("shrink-0", {
  variants: {
    color: {
      gray: "text-gray-500",
      brand: "text-brand-500",
      red: "text-red-600",
      green: "text-green-600",
    },
    size: {
      // Fun fact: the sizes that "feel right" here are near golden ratios with respect to the size of their containers
      micro: "size-3", // container 20px
      tiny: "size-3.5", // container 24px
      small: "size-[1.1875rem]", // container 32px
      medium: "size-6", // container 40px
      large: "size-[1.8125rem]", // container 48px
      huge: "size-[2.125rem]", // container 56px
    },
  },
  defaultVariants: {
    color: DEFAULT_COLOR,
    size: DEFAULT_SIZE,
  },
});

type AvatarVariantProps = VariantProps<typeof avatar>;

export interface Props extends AvatarVariantProps {
  src?: string;
  alt?: string;
  text?: string;
  icon?: React.ReactElement<{
    className?: string;
  }>;
  className?: string;
  classNames?: {
    root?: string;
    image?: string;
    text?: string;
  };
}

export const Avatar = ({
  src,
  alt,
  text,
  icon,
  variant = DEFAULT_VARIANT,
  color = DEFAULT_COLOR,
  size = DEFAULT_SIZE,
  className,
  classNames,
}: Props) => {
  const styledIcon = icon
    ? cloneElement(icon, {
        className: twMerge(iconVariants({ color, size }), icon.props.className),
      })
    : null;

  return (
    <div
      className={twMerge(
        avatar({ variant, color, size }),
        className,
        classNames?.root,
      )}
    >
      {src ? (
        <img
          className={twMerge(
            "inline-block h-full w-full object-cover",
            classNames?.image,
          )}
          src={src}
          alt={alt}
        />
      ) : icon ? (
        styledIcon
      ) : alt || text ? (
        <span
          className={twMerge(
            textVariants({ color, size }),
            text && size
              ? getCustomTextSizeClassName({ text, size })
              : undefined,
            classNames?.text,
          )}
        >
          {text ? text : alt ? getInitials(alt) : null}
        </span>
      ) : null}
    </div>
  );
};

/**
 * The text size classes for each size of Avatar.
 * The first element in the array is the class for text with 1-2 characters,
 * the second element is for text with 3 characters, and the third element is for text with 4 or more characters.
 */
const TEXT_SIZES_BY_AVATAR_SIZE: Record<
  Exclude<React.ComponentProps<typeof Avatar>["size"], null | undefined>,
  [string, string, string]
> = {
  micro: [
    "text-[.625rem] line-height-[.75rem]",
    "text-[.5rem] line-height-[.5rem]",
    "text-[.375rem] line-height-[.25rem]",
  ],
  tiny: [
    "text-xs",
    "text-[.625rem] line-height-[.75rem]",
    "text-[.5rem] line-height-[.5rem]",
  ],
  small: ["text-sm", "text-xs", "text-[.625rem] line-height-[.75rem]"],
  medium: ["text-base", "text-sm", "text-xs"],
  large: ["text-lg", "text-base", "text-sm"],
  huge: ["text-xl", "text-lg", "text-base"],
};

/**
 * Returns an appropriate text size class name based on the size of the text and the size of the Avatar.
 * In general this means reducing the size of the text as the length of the text increases.
 */
function getCustomTextSizeClassName({
  text,
  size,
}: {
  text: string;
  size: Exclude<React.ComponentProps<typeof Avatar>["size"], null | undefined>;
}) {
  const textSizesForAvatarSize = TEXT_SIZES_BY_AVATAR_SIZE[size];

  if (!textSizesForAvatarSize) {
    throw new Error(`Unsupported size: ${size}`);
  }

  if (text.length <= 2) {
    return textSizesForAvatarSize[0];
  } else if (text.length === 3) {
    return textSizesForAvatarSize[1];
  }

  return textSizesForAvatarSize[2];
}
